import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps, ReactNode } from 'react';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { getRewardImageUrl } from '@sorare/core/src/lib/rewards';

import type { BuyPackButton_cardPack } from './__generated__/index.graphql';

const StyledButton = styled(Button)`
  &.medium {
    min-width: 90px;
  }
  &.large {
    min-width: 100px;
  }
`;

const ShardsImg = styled.img`
  width: var(--double-unit);
  height: var(--double-unit);
  object-fit: contain;
`;
type Props = Omit<Partial<ComponentProps<typeof Button>>, 'children'> & {
  cardPack: Nullable<BuyPackButton_cardPack>;
  children?: (price: ReactNode) => ReactNode;
};

export const BuyPackButton = ({
  cardPack,
  children,
  onClick,
  ...otherProps
}: Props) => {
  const track = useEvents();
  const imageUrl =
    cardPack?.cardSet?.slug &&
    getRewardImageUrl('shard', Rarity.common, cardPack?.cardSet?.slug);

  const isGemsPack = cardPack?.currency === 'COMMON_GEM';
  const currencyIcon = isGemsPack ? (
    <GemIcon size={2.5} />
  ) : (
    !!imageUrl && (
      <ShardsImg
        src={proxyUrl(imageUrl, {
          cropWidth: 80,
        })}
        alt=""
        width={16}
        height={16}
      />
    )
  );
  const price = (
    <>
      {cardPack?.price} {currencyIcon}
    </>
  );
  return (
    <StyledButton
      size="large"
      color="primary"
      {...otherProps}
      onClick={
        onClick
          ? e => {
              onClick(e);
              if (cardPack) {
                track('Click Buy Pack', {
                  packSlug: cardPack?.slug,
                });
              }
            }
          : undefined
      }
    >
      <Horizontal center gap={0.5}>
        {children?.(price) || price}
      </Horizontal>
    </StyledButton>
  );
};
BuyPackButton.fragments = {
  cardPack: gql`
    fragment BuyPackButton_cardPack on CardPack {
      slug
      currency
      price
      cardSet {
        slug
      }
    }
  ` as TypedDocumentNode<BuyPackButton_cardPack>,
};
